import React, { useEffect } from "react";
import { Mic, Upload, Volume2, Headphones, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ListeningInstructionPage: React.FC = () => {
  const navigate = useNavigate();

  const handleStartTest = () => {
    navigate("/listening");
  };

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gradient-to-br from-red-50 to-red-200 min-h-screen flex items-center justify-center p-4">
      <div className="max-w-3xl w-full bg-white rounded-xl shadow-xl overflow-hidden transform transition-all">
        {/* Header with animated background */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-white rounded-full"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white rounded-full"></div>
          </div>
          <div className="inline-flex justify-center items-center w-20 h-20 rounded-full bg-white mb-4 shadow-md relative z-10">
            <Headphones className="text-red-600 w-12 h-12" />
          </div>
          <h1 className="text-3xl font-bold text-white relative z-10">
            Listening Assessment Instructions
          </h1>
          <p className="text-red-100 mt-2 relative z-10">
            Please read carefully before starting your assessment
          </p>
        </div>

        <div className="p-8">
          {/* Step 1 - Watch Video */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 p-5 mb-6 rounded-lg shadow-sm">
            <div className="flex items-start">
              <Volume2 className="text-blue-600 w-6 h-6 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-800 mb-2">
                  Step 1: Watch the Video
                </h3>
                <p className="text-blue-800">
                  Click the play button and watch the instructional video
                  carefully. Pay attention to all the details as you will need
                  to recall them later.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 - Record Audio */}
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-400 p-5 mb-6 rounded-lg shadow-sm">
            <div className="flex items-start">
              <Mic className="text-yellow-600 w-6 h-6 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-800 mb-2">
                  Step 2: Record Your Audio Response
                </h3>
                <p className="text-yellow-800">
                  After watching the video, click the record button and speak
                  clearly into your microphone. Describe what you learned from
                  the video in detail.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 - Upload & Submit */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 p-5 mb-6 rounded-lg shadow-sm">
            <div className="flex items-start">
              <Upload className="text-green-600 w-6 h-6 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-800 mb-2">
                  Step 3: Upload & Submit
                </h3>
                <p className="text-green-800">
                  Once you're satisfied with your recording, upload the audio
                  file and submit it for evaluation. Make sure your recording is
                  clear and complete.
                </p>
              </div>
            </div>
          </div>

          {/* Requirements section */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5 rounded-lg border-l-4 border-blue-400 mb-6 shadow-sm">
            <h3 className="font-semibold text-blue-800 mb-3">
              Before You Begin - Please Ensure:
            </h3>
            <ul className="space-y-3 pl-3">
              <li className="text-blue-800 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span>Microphone is working properly</span>
              </li>
              <li className="text-blue-800 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span>You are in a quiet environment</span>
              </li>
              <li className="text-blue-800 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span>Headphones or speakers are ready</span>
              </li>
              <li className="text-blue-800 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span>Stable internet connection</span>
              </li>
            </ul>
          </div>

          {/* Start Test Button */}
          <div className="text-center">
            <button
              onClick={handleStartTest}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 hover:from-red-600 hover:to-red-700"
            >
              Start Test
              <ArrowRight className="inline-block ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListeningInstructionPage;
